import {
  PricingStrategy,
  PricingTierPrice,
  ProductKey
} from '@atlassiansox/asc-product-catalog';
import {
  PricingBreakdown,
  PricingCalculation,
  PricingPeriod,
  reduceBreakdownToPricingCalculation
} from './models';
import { IProductPricingStore } from './product-pricing-store';
import {
  createPricingTierIsInUnitLimitsPredicate,
  getUnitPrice,
  PricingPlan
} from './utils';
import { createMarginalPerUnitBreakdown } from './utils/marginal-per-unit';

const DEFAULT_CURRENCY = 'USD';

export type PricingCalculatorOptions = {
  readonly productPricingStore: IProductPricingStore;
};

export class PricingCalculator {
  private readonly store: IProductPricingStore;

  constructor({ productPricingStore }: PricingCalculatorOptions) {
    this.store = productPricingStore;
  }

  async calculatePrice({
    productKey,
    units,
    period,
    currencyIsoCode
  }: GetPriceCalculationOptions): Promise<PricingCalculation> {
    const pricingPlan = await this.getPricingPlan({ productKey, period });

    if (
      !pricingPlan[0].prices.some(
        ({ currency }) => currency === currencyIsoCode
      )
    ) {
      currencyIsoCode = DEFAULT_CURRENCY;
    }

    return await this.calculatePriceWithPricingPlan({
      units,
      currencyIsoCode,
      pricingPlan
    });
  }

  async calculatePriceWithPricingPlan({
    units,
    currencyIsoCode,
    pricingPlan
  }: GetPriceCalculationWithPricingPlanOptions): Promise<PricingCalculation> {
    const breakdown = await this.getBreakdown({
      pricingPlan,
      units,
      currencyIsoCode
    });

    return breakdown.reduce(reduceBreakdownToPricingCalculation, {
      currencyIsoCode,
      breakdown,
      pricingPlan,
      total: 0
    });
  }

  private async getPricingPlan({
    productKey,
    period
  }: GetPricingPlanOptions): Promise<PricingPlan> {
    const {
      [PricingPeriod.MONTHLY]: monthly,
      [PricingPeriod.ANNUAL]: annual
    } = await this.store.getPricingPlans(productKey);

    // there are no "annual free" pricing plans but consumers of this package need to request
    // free pricing along with annual non-free pricing in one call so we need to return the
    // free monthly pricing plan always when the product key ends in '.free'
    const adjPeriod = productKey.endsWith('.free')
      ? PricingPeriod.MONTHLY
      : period;

    switch (adjPeriod) {
      case PricingPeriod.MONTHLY:
        if (monthly) {
          return monthly;
        } else {
          throw new Error(
            `Monthly pricing plan does not exist for product key: ${productKey}`
          );
        }
      case PricingPeriod.ANNUAL:
        if (annual) {
          return annual;
        } else {
          throw new Error(
            `Annual pricing plan does not exist for product key: ${productKey}`
          );
        }
    }
  }

  private async getBreakdown({
    pricingPlan,
    units,
    currencyIsoCode
  }: GetBreakdownOptions): Promise<PricingBreakdown> {
    const selectedTier = pricingPlan.find(
      createPricingTierIsInUnitLimitsPredicate(units)
    );
    if (selectedTier) {
      switch (selectedTier.pricingStrategy) {
        case PricingStrategy.MARGINAL_PER_UNIT:
          return createMarginalPerUnitBreakdown(
            pricingPlan,
            units,
            currencyIsoCode
          );
        default:
          return [
            {
              units,
              unitPrice: getUnitPrice(selectedTier, currencyIsoCode),
              tier: selectedTier
            }
          ];
      }
    } else {
      throw new Error(`Could not find pricing plan tier for ${units} units`);
    }
  }
}

export type GetPriceCalculationOptions = {
  readonly productKey: ProductKey;
  readonly units: number;
  readonly period: PricingPeriod;
  readonly currencyIsoCode: string;
};

export type GetPriceCalculationWithPricingPlanOptions = {
  readonly units: number;
  readonly currencyIsoCode: string;
  readonly pricingPlan: PricingPlan;
};

export type GetBreakdownOptions = {
  readonly pricingPlan: PricingPlan;
  readonly units: number;
  readonly currencyIsoCode: string;
};

export type GetPricingPlanOptions = {
  readonly productKey: ProductKey;
  readonly period: PricingPeriod;
};

export { PricingStrategy, PricingTierPrice, ProductKey };
